import { TutorialLayout, CodePreview } from '../../../../shared/components';
import { tutorialLevels } from '../../../../shared/config/tutorials';
import { Alert, AlertDescription } from '@voilajsx/uikit/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@voilajsx/uikit/card';
import { Palette, Info } from 'lucide-react';

const CustomThemes = () => {
  return (
    <TutorialLayout levels={tutorialLevels}>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold mb-4">Custom Themes</h1>
          <p className="text-lg text-muted-foreground">
            Create your own themes by defining custom color palettes.
          </p>
        </div>

        <Alert>
          <Palette className="w-4 h-4" />
          <AlertDescription>
            Custom themes let you match UIKit to your brand colors while maintaining the semantic color system.
          </AlertDescription>
        </Alert>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Theme Structure</h2>
          <p className="text-muted-foreground">
            A theme defines CSS custom properties for both light and dark modes:
          </p>

          <CodePreview
            title="Theme CSS Structure"
            code={`/* themes/brand.css */

/* Light mode colors */
[data-theme="brand"].light {
  --background: oklch(0.99 0.01 280);
  --foreground: oklch(0.15 0.02 280);

  --card: oklch(1 0 0);
  --card-foreground: oklch(0.15 0.02 280);

  --primary: oklch(0.55 0.25 280);       /* Your brand purple */
  --primary-foreground: oklch(1 0 0);

  --secondary: oklch(0.95 0.03 280);
  --secondary-foreground: oklch(0.25 0.05 280);

  --muted: oklch(0.96 0.02 280);
  --muted-foreground: oklch(0.5 0.03 280);

  --accent: oklch(0.95 0.05 280);
  --accent-foreground: oklch(0.25 0.05 280);

  --destructive: oklch(0.55 0.2 25);
  --destructive-foreground: oklch(1 0 0);

  --border: oklch(0.9 0.02 280);
  --input: oklch(0.9 0.02 280);
  --ring: oklch(0.55 0.25 280);
}

/* Dark mode colors */
[data-theme="brand"].dark {
  --background: oklch(0.15 0.03 280);
  --foreground: oklch(0.95 0.01 280);

  --card: oklch(0.18 0.03 280);
  --card-foreground: oklch(0.95 0.01 280);

  --primary: oklch(0.65 0.2 280);
  --primary-foreground: oklch(0.1 0.02 280);

  /* ... rest of dark mode colors */
}`}
            language="css"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Understanding OKLCH</h2>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">OKLCH Color Format</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="font-mono bg-muted p-3 rounded">
                oklch(L C H)
                <br />
                oklch(0.55 0.25 280)
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-4">
                  <div className="w-20 font-semibold">L (0-1)</div>
                  <div className="text-muted-foreground">Lightness - 0 is black, 1 is white</div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-20 font-semibold">C (0-0.4)</div>
                  <div className="text-muted-foreground">Chroma - saturation/vibrancy</div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-20 font-semibold">H (0-360)</div>
                  <div className="text-muted-foreground">Hue angle - the actual color</div>
                </div>
              </div>

              <div className="grid grid-cols-6 gap-2 pt-4">
                <div className="text-center">
                  <div className="h-8 rounded" style={{ background: 'oklch(0.6 0.2 0)' }}></div>
                  <div className="text-xs mt-1">0° Red</div>
                </div>
                <div className="text-center">
                  <div className="h-8 rounded" style={{ background: 'oklch(0.6 0.2 60)' }}></div>
                  <div className="text-xs mt-1">60° Yellow</div>
                </div>
                <div className="text-center">
                  <div className="h-8 rounded" style={{ background: 'oklch(0.6 0.2 120)' }}></div>
                  <div className="text-xs mt-1">120° Green</div>
                </div>
                <div className="text-center">
                  <div className="h-8 rounded" style={{ background: 'oklch(0.6 0.2 180)' }}></div>
                  <div className="text-xs mt-1">180° Cyan</div>
                </div>
                <div className="text-center">
                  <div className="h-8 rounded" style={{ background: 'oklch(0.6 0.2 240)' }}></div>
                  <div className="text-xs mt-1">240° Blue</div>
                </div>
                <div className="text-center">
                  <div className="h-8 rounded" style={{ background: 'oklch(0.6 0.2 300)' }}></div>
                  <div className="text-xs mt-1">300° Purple</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Step-by-Step: Creating a Theme</h2>

          <div className="space-y-6">
            <div>
              <h3 className="font-semibold mb-2">1. Choose Your Brand Color</h3>
              <CodePreview
                title="Define primary hue"
                code={`/* Pick a hue angle for your brand */
/* Example: Purple brand = 280° */
--primary: oklch(0.55 0.25 280);`}
                language="css"
              />
            </div>

            <div>
              <h3 className="font-semibold mb-2">2. Create the CSS File</h3>
              <CodePreview
                title="src/themes/brand.css"
                code={`[data-theme="brand"].light {
  /* Base colors - keep neutral with slight tint */
  --background: oklch(0.99 0.01 280);
  --foreground: oklch(0.15 0.02 280);

  /* Primary - your brand color */
  --primary: oklch(0.55 0.25 280);
  --primary-foreground: oklch(1 0 0);

  /* Secondary - desaturated version */
  --secondary: oklch(0.95 0.03 280);
  --secondary-foreground: oklch(0.25 0.05 280);

  /* Keep destructive as red-ish */
  --destructive: oklch(0.55 0.2 25);
  --destructive-foreground: oklch(1 0 0);
}

[data-theme="brand"].dark {
  /* Invert lightness values for dark mode */
  --background: oklch(0.15 0.03 280);
  --foreground: oklch(0.95 0.01 280);

  --primary: oklch(0.65 0.2 280);
  --primary-foreground: oklch(0.1 0.02 280);
}`}
                language="css"
              />
            </div>

            <div>
              <h3 className="font-semibold mb-2">3. Import the Theme</h3>
              <CodePreview
                title="src/index.css"
                code={`@import "tailwindcss";
@import "@voilajsx/uikit/styles";
@import "./themes/brand.css";`}
                language="css"
              />
            </div>

            <div>
              <h3 className="font-semibold mb-2">4. Use the Theme</h3>
              <CodePreview
                title="main.tsx"
                code={`<ThemeProvider defaultTheme="brand" defaultMode="light">
  <App />
</ThemeProvider>`}
                language="tsx"
              />
            </div>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Color Contrast Tips</h2>

          <Alert>
            <Info className="w-4 h-4" />
            <AlertDescription>
              For accessibility, ensure sufficient contrast between background and foreground colors. OKLCH makes this easier since lightness is perceptually uniform.
            </AlertDescription>
          </Alert>

          <ul className="space-y-2 text-muted-foreground">
            <li>• <strong>Foreground on light bg:</strong> Use L values below 0.4</li>
            <li>• <strong>Foreground on dark bg:</strong> Use L values above 0.85</li>
            <li>• <strong>Primary colors:</strong> L around 0.5-0.6 works for both modes</li>
            <li>• <strong>Muted text:</strong> L around 0.5 for subtle but readable text</li>
          </ul>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Extending Existing Themes</h2>
          <p className="text-muted-foreground">
            You can also override just a few colors from an existing theme:
          </p>

          <CodePreview
            title="Override primary color only"
            code={`/* Override default theme's primary */
[data-theme="default"].light {
  --primary: oklch(0.55 0.25 150);  /* Change to green */
  --ring: oklch(0.55 0.25 150);
}

[data-theme="default"].dark {
  --primary: oklch(0.65 0.2 150);
  --ring: oklch(0.65 0.2 150);
}`}
            language="css"
          />
        </section>
      </div>
    </TutorialLayout>
  );
};

export default CustomThemes;
